import { formatCurrency, pluralize } from "@gigpro/utils";
import { formatDate } from "@gigpro/utils/datetime";
import type { JobsCancelSummary, JobsPlanSwitchPreview, JobsSubscriptionStatus } from "@sidegigllc/api/client";

export const JOBS_PLAN_AMOUNT = "$250";
export const JOBS_PLAN_PRICE = `${JOBS_PLAN_AMOUNT}/month`;
export const JOBS_TRIAL_DAYS = 7;
/** Staff can always see applicants, so without this they wonder why an unpaid location shows them. */
export const JOBS_STAFF_APPLICANTS_NOTE =
  "As Qwick staff you can still see applicants here. The business needs a Jobs plan to see them.";

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Oct 2"; the server sends these dates as null until they apply. */
export const day = (date: Date | null | undefined) => (date ? formatDate(date, "MMM d") : "-");

/** "Oct 2, 2026", for billing history. */
export const dayWithYear = (date: Date) => formatDate(date, "MMM d, yyyy");

const CARD_BRANDS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  american_express: "American Express",
  discover: "Discover",
};

/** "Visa ending 4242", "bank account ending 6789", "Monthly Qwick invoice", or null when nothing is on file. */
export const jobsPaymentLabel = (status: JobsSubscriptionStatus): string | null => {
  if (status.collection === "INVOICE") return "Monthly Qwick invoice";
  if (!status.payment_method_last4) return null;
  // Banks send their bank name as the brand, often in capitals, so they get a generic label.
  const name =
    status.payment_method_type === "us_bank_account"
      ? "bank account"
      : (CARD_BRANDS[status.payment_method_brand?.toLowerCase() ?? ""] ?? "card");
  return `${name} ending ${status.payment_method_last4}`;
};

/** A charge needs a card or bank on file, unless the location pays by invoice. */
export const jobsNeedsPaymentMethod = (status: JobsSubscriptionStatus) =>
  status.collection !== "INVOICE" && (status.needs_payment_method || !jobsPaymentLabel(status));

const withDate = (label: string, date: Date | null) => (date ? `${label} ${day(date)}` : null);

/** The one-line status on the plan card. */
export const jobsPlanStatusLabel = (status: JobsSubscriptionStatus): string => {
  switch (status.state) {
    case "TRIALING":
      return withDate("Free trial until", status.trial_ends_at) ?? "Free trial";
    case "ACTIVE":
      return withDate("Active, renews", status.access_until) ?? "Active";
    case "PAST_DUE":
      return "Payment failed. We're retrying.";
    case "CANCELING":
      return withDate("Canceled, ends", status.access_until) ?? "Canceled";
    case "LAPSED":
    case "ARCHIVED": {
      const ended = withDate("Ended", status.access_until) ?? "Ended";
      const closes = status.state === "LAPSED" && withDate("Jobs close on", status.lapse_ends_at);
      return closes ? `${ended}. ${closes}` : ended;
    }
    default:
      return status.can_start_trial ? "Free trial available" : "Not subscribed";
  }
};

export type JobsPlanBannerCopy = {
  severity: "info" | "warning" | "error";
  message: string;
  action: "manage" | "update-payment" | "resubscribe";
  actionLabel: string;
};

const trialPrefix = (ends: Date | null, ended: boolean, now: Date) => {
  if (ended) return `Your free trial ended on ${day(ends)}.`;
  const daysLeft = ends ? Math.max(0, Math.ceil((ends.getTime() - now.getTime()) / DAY_MS)) : null;
  if (daysLeft === 0) return "Your free trial ends today.";
  if (daysLeft) return `Free trial: ${daysLeft} ${pluralize(daysLeft, "day")} left.`;
  return "You're on a free trial.";
};

const trialChargeNote = (status: JobsSubscriptionStatus, ends: Date | null, ended: boolean) => {
  const invoiced = status.collection === "INVOICE";
  if (ended) {
    return invoiced
      ? ` ${JOBS_PLAN_PRICE} now goes on your monthly Qwick invoice.`
      : ` Your first ${JOBS_PLAN_PRICE} charge is on its way.`;
  }
  if (!ends) return "";
  return invoiced
    ? ` From ${day(ends)}, ${JOBS_PLAN_PRICE} goes on your monthly Qwick invoice.`
    : ` Your first ${JOBS_PLAN_PRICE} charge is on ${day(ends)}.`;
};

const trialBanner = (status: JobsSubscriptionStatus, now: Date): JobsPlanBannerCopy => {
  const ends = status.trial_ends_at;
  // The daily sweep ends a trial, so for up to a day the state can still say TRIALING after it ended.
  const ended = ends !== null && ends.getTime() < now.getTime();
  const prefix = trialPrefix(ends, ended, now);

  if (status.collection !== "INVOICE" && status.needs_payment_method) {
    // Stripe cancels a trial with no payment method at its end: nothing is charged and applicants are hidden.
    let by = "before your trial ends";
    if (ended) by = "now";
    else if (ends) by = `by ${day(ends)}`;
    return {
      severity: "warning",
      message: `${prefix} Add a payment method ${by} to keep seeing your applicants.`,
      action: "update-payment",
      actionLabel: "Add payment method",
    };
  }
  const next = trialChargeNote(status, ends, ended);
  return { severity: "info", message: `${prefix}${next}`, action: "manage", actionLabel: "Manage plan" };
};

/** Banner above the hire pages. Null for states with nothing to say (legacy, not subscribed, active, archived). */
export const jobsPlanBanner = (
  status: JobsSubscriptionStatus | undefined,
  now = new Date(),
): JobsPlanBannerCopy | null => {
  if (status?.plan !== "SUBSCRIPTION") return null;
  switch (status.state) {
    case "TRIALING":
      return trialBanner(status, now);
    case "CANCELING":
      return {
        severity: "info",
        message: status.access_until
          ? `Your Jobs plan ends on ${day(status.access_until)}. You won't be charged again. After that, your applicants are hidden.`
          : "Your Jobs plan is canceled. You won't be charged again. When it ends, your applicants are hidden.",
        action: "manage",
        actionLabel: "Manage plan",
      };
    case "PAST_DUE":
      return {
        severity: "error",
        message: `We couldn't charge ${(status.collection !== "INVOICE" && jobsPaymentLabel(status)) || "your payment method"} for your Jobs plan. Update your payment method to keep your plan.`,
        action: "update-payment",
        actionLabel: "Update payment method",
      };
    case "LAPSED":
      return {
        severity: "warning",
        message: status.lapse_ends_at
          ? `Your Jobs plan ended, so your applicants are hidden. Your jobs close on ${day(status.lapse_ends_at)} unless you resubscribe.`
          : "Your Jobs plan ended, so your applicants are hidden. Resubscribe to keep your jobs open.",
        action: "resubscribe",
        actionLabel: "Resubscribe",
      };
    default:
      return null;
  }
};

export type JobsTeaserCopy = {
  title: string;
  body: string;
  footnote?: string;
  action: "trial" | "resubscribe" | null;
  actionLabel?: string;
};

type TeaserCounts = {
  locationName: string;
  /** Applicants at the location, or on the job when `onJobPage`. Undefined while loading. */
  applicants?: number;
  onJobPage?: boolean;
  trialEndsAt: Date;
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const trialTitle = ({ applicants, onJobPage, locationName }: TeaserCounts) => {
  if (applicants === undefined) return "Your applicants are hidden";
  const count = plural(applicants, "applicant", "applicants");
  return onJobPage ? `This job has ${count}` : `You have ${count} at ${locationName}`;
};

const resubscribeBody = (status: JobsSubscriptionStatus, locationName: string) => {
  // Staff moved it onto the Jobs plan after its trial was used: nothing ended and its jobs are still open.
  if (status.state === "NOT_SUBSCRIBED") {
    return `Your applicants at ${locationName} are hidden. Resubscribe to see them.`;
  }
  const ended = status.access_until ? ` ended on ${day(status.access_until)}` : " ended";
  if (status.state !== "LAPSED") {
    return `Your Jobs plan${ended} and your jobs have closed. Your applicants are saved. Resubscribe to see them.`;
  }
  const closes = status.lapse_ends_at
    ? `Your jobs stay open until ${day(status.lapse_ends_at)}, then close.`
    : "Resubscribe to keep your jobs open.";
  return `Your Jobs plan at ${locationName}${ended}, so your applicants are hidden. ${closes}`;
};

/** The call to action while a location's applicants are hidden. Null when it sees them. */
/** A team member can't start or restart the plan, so the teaser asks them to get an admin instead. */
export const askAnAdmin = (copy: JobsTeaserCopy, locationName: string): JobsTeaserCopy =>
  copy.action === null
    ? copy
    : {
        title: copy.action === "resubscribe" ? "Your applicants are hidden" : copy.title,
        body: `Ask an admin at ${locationName} to ${copy.action === "trial" ? "start the free trial" : "resubscribe"}.`,
        action: null,
      };

export const jobsTeaserCopy = (
  status: JobsSubscriptionStatus | undefined,
  counts: TeaserCounts,
): JobsTeaserCopy | null => {
  if (status?.access !== "TEASER") return null;
  if (status.can_start_trial) {
    const footnote = `Free until ${day(counts.trialEndsAt)}. Cancel anytime before then.`;
    const trial = { action: "trial", actionLabel: "Start free trial", footnote } as const;
    if (counts.applicants === 0) {
      return {
        ...trial,
        title: counts.onJobPage ? "Your job is live on Qwick" : "Your jobs are live on Qwick",
        body: "Start a free trial to see applicants as they come in.",
      };
    }
    return {
      ...trial,
      title: trialTitle(counts),
      body: `Start a 7-day free trial to see who they are, message them and schedule interviews. Then ${JOBS_PLAN_PRICE}.`,
    };
  }
  if (status.can_resubscribe) {
    return {
      title: counts.applicants
        ? `Resubscribe to see your ${plural(counts.applicants, "applicant", "applicants")}`
        : "Resubscribe to see your applicants",
      body: resubscribeBody(status, counts.locationName),
      action: "resubscribe",
      actionLabel: `Resubscribe for ${JOBS_PLAN_PRICE}`,
      footnote:
        status.collection === "INVOICE"
          ? "Goes on your monthly Qwick invoice from today. No free trial."
          : "Charged today. No free trial.",
    };
  }
  return {
    title: "Your applicants are hidden",
    body: "Contact Qwick support to set up a Jobs plan for this location.",
    action: null,
  };
};

/** True while the location's applicants are hidden. Qwick staff are never locked out, as on the API. */
export const isJobsLocked = (status: JobsSubscriptionStatus | undefined, isStaff: boolean) =>
  !isStaff && status?.access === "TEASER";

export type JobsGate = "trial" | "resubscribe" | "askAdmin";

/**
 * The one question every Jobs surface asks. Null when nothing gates the person. A locked location with neither
 * option (Qwick support sets up its plan) has nothing to offer, so it is null here: use `isJobsLocked` to ask
 * whether applicants are hidden.
 */
export const jobsGateFor = (
  status: JobsSubscriptionStatus | undefined,
  { isStaff, canManage }: { isStaff: boolean; canManage: boolean },
): JobsGate | null => {
  if (!status || !isJobsLocked(status, isStaff)) return null;
  if (!status.can_start_trial && !status.can_resubscribe) return null;
  if (!canManage) return "askAdmin";
  return status.can_start_trial ? "trial" : "resubscribe";
};

/**
 * The 30-day clock line for a job posted without a plan. Nothing when the job has no `free_until` (full access, staff,
 * crawled jobs) or the person isn't gated, so it never shows for a location that already has a plan.
 */
export const jobClockCopy = (freeUntil: Date | null | undefined, gate: JobsGate | null): string | null => {
  if (!freeUntil || !gate) return null;
  const unless: Record<JobsGate, string> = {
    trial: "you start your free trial",
    resubscribe: "you resubscribe",
    askAdmin: "an admin starts a Jobs plan",
  };
  return `This job stays up until ${day(freeUntil)} unless ${unless[gate]}`;
};

export type JobsPlanAction = "trial" | "cancel" | "keep" | "resubscribe";

/** The plan card's buttons for the plan's state. */
export const jobsPlanActions = (status: JobsSubscriptionStatus): Array<JobsPlanAction> => {
  if (status.plan !== "SUBSCRIPTION") return [];
  if (status.can_start_trial) return ["trial"];
  if (status.can_resubscribe) return ["resubscribe"];
  if (status.state === "CANCELING") return ["keep"];
  if (status.state === "TRIALING" || status.state === "ACTIVE" || status.state === "PAST_DUE") return ["cancel"];
  return [];
};

export type JobsCancelCopy = { title: string; body: string; counts?: string; warning?: string };

const closingJobs = (summary: JobsCancelSummary | undefined) => {
  if (!summary) return "Your open jobs stay open for 14 more days so pros can still apply, then they close.";
  if (summary.open_jobs === 0) return "";
  if (summary.open_jobs === 1)
    return "Your 1 open job stays open for 14 more days so pros can still apply, then it closes.";
  return `Your ${summary.open_jobs} open jobs stay open for 14 more days so pros can still apply, then they close.`;
};

const cancelBody = (status: JobsSubscriptionStatus, summary: JobsCancelSummary | undefined) => {
  const resubscribe = "You can resubscribe anytime, with no free trial.";
  if (status.state === "TRIALING") {
    const runs = status.trial_ends_at ? `runs until ${day(status.trial_ends_at)}` : "keeps running";
    const after =
      summary?.open_jobs === 0
        ? "After that, your applicants are hidden."
        : "After that, your applicants are hidden, and your jobs stay open for 14 more days, then close.";
    return `Your free trial ${runs} and you won't be charged. ${after}`;
  }
  const jobs = closingJobs(summary);
  if (status.state === "PAST_DUE") {
    // Canceling only stops renewal: Stripe keeps retrying the failed invoice until the period ends.
    const ends = status.access_until
      ? `Your plan ends on ${day(status.access_until)}.`
      : "Your plan ends at the end of this billing period.";
    return [
      ends,
      "Until then, we'll keep trying the payment that failed and you'll keep full access.",
      "You won't be charged after that, and your applicants will be hidden.",
      jobs,
      resubscribe,
    ]
      .filter(Boolean)
      .join(" ");
  }
  const until = status.access_until ? `until ${day(status.access_until)}` : "until the end of this billing period";
  return [
    `You'll keep full access ${until} and won't be charged again.`,
    "After that, your applicants are hidden.",
    jobs,
    resubscribe,
  ]
    .filter(Boolean)
    .join(" ");
};

export const jobsCancelCopy = (
  status: JobsSubscriptionStatus,
  locationName: string,
  summary: JobsCancelSummary | undefined,
): JobsCancelCopy => {
  const title = `Cancel your Jobs plan at ${locationName}?`;
  const body = cancelBody(status, summary);
  if (!summary) return { title, body };
  const { open_jobs, applicants, scheduled_interviews, upcoming_working_interviews } = summary;
  const warning = [
    upcoming_working_interviews &&
      `${plural(upcoming_working_interviews, "paid working interview is", "paid working interviews are")} still scheduled and will go ahead.`,
    open_jobs &&
      "When your jobs close, any offers still out are withdrawn and any screening calls still scheduled are canceled. We'll let the pros know.",
  ]
    .filter(Boolean)
    .join(" ");
  return {
    title,
    body,
    counts:
      open_jobs || applicants || scheduled_interviews
        ? `${plural(open_jobs, "open job", "open jobs")} · ${plural(applicants, "applicant", "applicants")} · ${plural(scheduled_interviews, "screening call", "screening calls")} scheduled`
        : undefined,
    warning: warning || undefined,
  };
};

/** The footnote on each hidden applicant's card. */
export const hiddenApplicantNote = (status: JobsSubscriptionStatus | undefined) => {
  if (status?.can_start_trial) return "Start a free trial to see who this is.";
  if (status?.can_resubscribe) return "Resubscribe to see who this is.";
  return "Set up a Jobs plan to see who this is.";
};

export type StaffPlanSwitchCopy = { title: string; lines: Array<string>; refund?: string; confirm: string };

/** Qwick staff: what moving a location to pay per hire does. Refunds stay manual. */
export const staffPayPerHireCopy = (preview: JobsPlanSwitchPreview, locationName: string): StaffPlanSwitchCopy => {
  let ends = "Their Jobs plan ends today. Stripe stops billing now, with no proration.";
  if (!preview.has_open_plan) ends = "They have no open Jobs plan, so nothing is billed or canceled.";
  else if (preview.collection === "INVOICE")
    ends = "Their Jobs plan ends today, and this month's charge won't go on their Qwick invoice.";
  const refund =
    preview.suggested_refund_cents > 0
      ? `Nothing is refunded automatically. If you agreed to a refund, the unused part is about ${formatCurrency(preview.suggested_refund_cents / 100)} (${plural(preview.unused_days, "day", "days")} left of the ${formatCurrency(preview.last_charge_cents / 100)} they paid). Refund it in Stripe.`
      : undefined;
  const unpaid =
    preview.unpaid_cents > 0
      ? `Their unpaid ${formatCurrency(preview.unpaid_cents / 100)} is voided, so they won't be charged for it.`
      : undefined;
  return {
    title: `Move ${locationName} to pay per hire?`,
    lines: [ends, unpaid, "They keep seeing their applicants, and new offers carry the hire fee."].filter(
      (line): line is string => !!line,
    ),
    refund,
    confirm: "Move to pay per hire",
  };
};

/** What a location sees on the Jobs plan before it has a trial or subscription. */
const whatTheyWillSee = ({ open_jobs, applicants, pending_interviews, pending_offers }: JobsPlanSwitchPreview) =>
  `Until then, ${plural(open_jobs, "open job shows", "open jobs show")} only the top 3 applicants and ${plural(applicants, "applicant is", "applicants are")} blurred. ${plural(pending_interviews, "pending interview", "pending interviews")} and ${plural(pending_offers, "pending offer", "pending offers")} on those jobs will need the plan.`;

/** Qwick staff: what moving a location to the Jobs plan does, with or without starting its free trial. */
export const staffJobsPlanCopy = (
  preview: JobsPlanSwitchPreview,
  locationName: string,
  startTrial: boolean,
  /** The location's own billing: preview.collection is null for a location with no open plan. */
  collection: JobsSubscriptionStatus["collection"],
): StaffPlanSwitchCopy => {
  const lines = [`They'll be on the Jobs plan at ${JOBS_PLAN_PRICE}.`];
  if (!preview.trial_available)
    lines.push(
      "They already used their free trial, so they'll be asked to resubscribe. Their applicants are hidden until they do.",
      whatTheyWillSee(preview),
    );
  else if (!startTrial)
    lines.push("Their applicants are hidden until they start their free trial.", whatTheyWillSee(preview));
  else if (collection === "INVOICE")
    lines.push(
      `Their 7-day free trial starts now, then ${JOBS_PLAN_PRICE} goes on their monthly Qwick invoice unless they cancel. Only start it if they asked.`,
    );
  else if (preview.needs_payment_method)
    lines.push(
      "Their 7-day free trial starts now. They have no payment method, so it ends after 7 days unless they add one.",
    );
  else
    lines.push(
      "Their 7-day free trial starts now, and their card is charged after 7 days unless they cancel. Only start it if they asked.",
    );
  return { title: `Move ${locationName} to the Jobs plan?`, lines, confirm: "Move to Jobs plan" };
};

/** What a pay-per-hire location can switch to when it hires: the trial, or resubscribing once the trial was used. */
export type PayPerHireSwitch = "trial" | "resubscribe";

/**
 * Null for a location that is not on pay per hire. Mirrors the API, which starts the trial before it resubscribes.
 * Resubscribing charges now and the hire dialog can't collect a card, so a location with none keeps the fee only.
 */
export const payPerHireSwitchFor = (status: JobsSubscriptionStatus | undefined): PayPerHireSwitch | null => {
  if (!status) return null;
  if (status.can_switch_with_trial) return "trial";
  if (status.can_switch_with_resubscribe && !jobsNeedsPaymentMethod(status)) return "resubscribe";
  return null;
};

export type PayPerHireChoiceCopy = { label: string; body: string; oneWayDoor: string; askAdmin: string };

/** The hire dialog's second option for a pay-per-hire location, with the one-way door said plainly. */
export const payPerHireChoiceCopy = (action: PayPerHireSwitch, locationName: string): PayPerHireChoiceCopy => ({
  label: action === "trial" ? "Start the free trial and hire for no fee" : `Resubscribe (${JOBS_PLAN_PRICE})`,
  body:
    action === "trial"
      ? `Your first 7 days are free, then ${JOBS_PLAN_PRICE}. Hiring is included in the Jobs plan.`
      : `Your free trial was already used, so you pay ${JOBS_PLAN_PRICE} now. Hiring is included in the Jobs plan.`,
  oneWayDoor: `This moves ${locationName} off pay per hire. If you cancel later, the plan lapses and your open jobs close after 14 days. Only Qwick staff can move a location back to pay per hire.`,
  askAdmin: "Ask an admin about the Jobs plan.",
});

/**
 * The applicant the profile sheet may request. Like the list queries it waits for the plan: a locked location must
 * never request an applicant. Its top applicants are the exception (the API opens their reads), so for a locked
 * location the sheet opens for those ids only.
 */
export const sheetApplicantId = ({
  planKnown,
  locked,
  applicantId,
  topApplicantIds,
}: {
  planKnown: boolean;
  locked: boolean | undefined;
  applicantId: string | undefined;
  topApplicantIds: ReadonlyArray<string>;
}) => {
  if (!planKnown || !applicantId) return;
  if (!locked) return applicantId;
  return topApplicantIds.includes(applicantId) ? applicantId : undefined;
};

export type PostFreeChoiceCopy = { planLine: string; planActionLabel: string };

/**
 * What the "post free" card says about the plan, for a location whose gate is the trial or Resubscribe (an
 * `askAdmin` gate says which of the two the admin would pick through `canStartTrial`).
 */
export const postFreeChoiceCopy = (gate: JobsGate, canStartTrial: boolean): PostFreeChoiceCopy => {
  const trial = gate === "trial" || (gate === "askAdmin" && canStartTrial);
  return trial
    ? {
        planLine: `Comes with the Jobs plan: ${JOBS_TRIAL_DAYS}-day free trial, then ${JOBS_PLAN_PRICE}.`,
        planActionLabel: "Start free trial & add working interviews",
      }
    : {
        planLine: `Comes with the Jobs plan: ${JOBS_PLAN_PRICE}, no free trial.`,
        planActionLabel: "Resubscribe & add working interviews",
      };
};
